import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TalentBasicInfo } from "@/features/talent/components/talent-detail/TalentBasicInfo";
import { useTalentInfo, updateTalentApi } from "@/api/services/main/talent";
import { convertResToFormData, convertTalentFormDataReq, isValueDifferent } from "@/features/talent/utils/convertTalentFormData";
import { FCC } from "@/types/common";
import { Stack, Box, Modal, Typography, styled, Grid } from "@mui/material";
import { IconClose, IconEditor, IconSave } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { TalentEducationBackground } from "@/features/talent/components/talent-detail/TalentEducationBackground";
import { TalentFamilyInfo } from "@/features/talent/components/talent-detail/TalentFamilyInfo";
import { TalentInterviewEvaluation } from "@/features/talent/components/talent-detail/TalentInterviewEvaluation";
import { TalentLanguageAbility } from "@/features/talent/components/talent-detail/TalentLanguageAbility";
import { TalentMemo } from "@/features/talent/components/talent-detail/TalentMemo";
import { TalentResidenceStatusInfo } from "@/features/talent/components/talent-detail/TalentResidenceStatusInfo";
import { TalentWorkExperience } from "@/features/talent/components/talent-detail/TalentWorkExperience";
import { TalentCertificateFiles } from "@/features/talent/components/talent-detail/TalentCertificateFiles";
import { useState, useEffect, useMemo, useRef } from "react";
import { Modal as ConfirmModal } from "@/components/elements/Modal";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetTalentSchema } from "@/features/talent/hooks/useGetTalentSchema";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { Form } from "@/components/elements/Form";
import { TalentFormBasicInfo } from "@/features/talent/components/talent-form/TalentFormBasicInfo";
import { TalentFormResidenceStatus } from "@/features/talent/components/talent-form/TalentFormResidenceStatus";
import { TalentFormLanguageAbility } from "@/features/talent/components/talent-form/TalentFormLanguageAbility";
import { TalentFormCertificateFiles } from "@/features/talent/components/talent-form/TalentFormCertificateFiles";
import { TalentFormEducationBackground } from "@/features/talent/components/talent-form/TalentFormEducationBackground";
import { TalentFormWorkExperience } from "@/features/talent/components/talent-form/TalentFormWorkExperience";
import { TalentFormMemo } from "@/features/talent/components/talent-form/TalentFormMemo";
import { TalentFormInterviewEvaluation } from "@/features/talent/components/talent-form/TalentInterviewEvaluation";
import { TalentFormFamilyInfo } from "@/features/talent/components/talent-form/TalentFormFamilyInfo";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { useGetLanguageTestList, useGetSpecificSkillCertificateList } from "@/api/services/global/master";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useParams } from "react-router-dom";
import { TalentFormRecruitmentStatus } from "@/features/talent/components/talent-form/TalentFormRecruitmentStatus";
import { TalentRecruitmentStatus } from "@/features/talent/components/talent-detail/TalentRecruitmentStatus";
import { scrollToFirstError } from "@/utils/scrollToFirstError";

type TTalentInfoModalProps = {
  isOpen: boolean;
  talentId?: number;
  onClose: () => void;
};

export const TalentInfoModal: FCC<TTalentInfoModalProps> = ({ isOpen, talentId, onClose }) => {
  const { data, isLoading, refetch } = useTalentInfo(talentId);
  const queryClient = useQueryClient();
  const { caseId } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingEditMode, setIsLoadingEditMode] = useState(false);
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);
  const [pendingAction, setPendingAction] = useState<"cancel" | "close" | null>(null);
  const [isChangeValue, setIsChangeValue] = useState(false);
  const { talentSchema } = useGetTalentSchema();
  const { data: languageTestData, isLoading: isLoadingLanguageTest } = useGetLanguageTestList();
  const { data: specificSkillCertificateData } = useGetSpecificSkillCertificateList();
  const initialValuesRef = useRef<Partial<TTalentForm> | null>(null);
  const isFormInitializedRef = useRef(false);
  // Ref to the certificateFiles area — used as the scroll anchor for errors on the unregistered
  // `relatedFileTypes` / `displayFiles` fields (whose yup errors carry no usable DOM ref).
  const certificateFilesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setIsEditMode(false);
      setIsLoadingEditMode(false);
      setShowConfirmLeave(false);
      setPendingAction(null);
      setIsChangeValue(false);
      initialValuesRef.current = null;
      isFormInitializedRef.current = false;
    }
  }, [isOpen]);

  // Memoize talentData to prevent unnecessary re-renders that could reset form
  const talentData = useMemo(() => (data?.data ? convertResToFormData(data.data) : undefined), [data?.data]);

  const methods = useForm<TTalentForm>({
    defaultValues: {
      workExperiences: [
        {
          jobType: "",
          companyName: "",
          startDate: null,
          endDate: null,
        },
      ],
      ...talentData,
    },
    resolver: yupResolver(talentSchema as typeof talentSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const watchValue = methods.watch();

  // Track form changes - similar to TalentForm
  useEffect(() => {
    if (!isEditMode || !isFormInitializedRef.current) {
      return;
    }

    if (!initialValuesRef.current) {
      setIsChangeValue(false);
      return;
    }

    const hasChanged = Object.keys(initialValuesRef.current).some((key) => {
      const currentValue = watchValue[key as keyof TTalentForm];
      const initialValue = initialValuesRef.current![key as keyof TTalentForm];
      return isValueDifferent(currentValue, initialValue);
    });

    setIsChangeValue(hasChanged);
  }, [watchValue, isEditMode]);

  useEffect(() => {
    if (talentData && !isEditMode) {
      methods.reset({
        workExperiences: talentData.workExperiences || [
          {
            jobType: "",
            companyName: "",
            startDate: null,
            endDate: null,
          },
        ],
        ...talentData,
      });
    }
  }, [talentData, isEditMode, methods]);

  useEffect(() => {
    if (!isEditMode || !isOpen) return;

    const handleFormSubmit = (e: SubmitEvent) => {
      const target = e.target as HTMLFormElement;
      if (target && target.closest('[data-modal-form="talent-info"]')) {
        e.stopPropagation();
      }
    };

    document.addEventListener("submit", handleFormSubmit, true);

    return () => {
      document.removeEventListener("submit", handleFormSubmit, true);
    };
  }, [isEditMode, isOpen]);

  useLocationInfo({ methods, names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" } });

  // Scroll to first errored field (top-to-bottom DOM order) on every submit attempt.
  // Use submitCount (primitive, increments per attempt) instead of the errors object reference,
  // because RHF's formState.errors Proxy often keeps the same reference across submits — a
  // useEffect with `errors` in deps would not re-fire reliably.
  const submitCount = methods.formState.submitCount;
  useEffect(() => {
    if (!submitCount) return;
    setTimeout(() => {
      scrollToFirstError(methods.formState.errors, {
        customAnchors: {
          relatedFileTypes: certificateFilesRef.current,
          displayFiles: certificateFilesRef.current,
        },
      });
    }, 100);
  }, [submitCount, methods]);

  const recruitmentStatus = useWatch({ control: methods.control, name: "recruitmentStatus" });
  const prevRecruitmentStatusRef = useRef(recruitmentStatus);
  useEffect(() => {
    if (prevRecruitmentStatusRef.current !== recruitmentStatus) {
      prevRecruitmentStatusRef.current = recruitmentStatus;
      const conditionalFields: (keyof TTalentForm)[] = ["matchOfficeId", "nationalityId", "gender", "location", "companySpecificIndustryId"];
      methods.trigger(conditionalFields);
    }
  }, [recruitmentStatus, methods]);

  const handleEdit = async () => {
    setIsLoadingEditMode(true);
    try {
      if (isLoadingLanguageTest || !languageTestData || languageTestData.length === 0) {
        const maxWaitTime = 2000;
        const checkInterval = 100;
        const maxAttempts = maxWaitTime / checkInterval;

        for (let i = 0; i < maxAttempts; i++) {
          await new Promise((resolve) => setTimeout(resolve, checkInterval));
          if (i >= 5) {
            break;
          }
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
      setIsEditMode(true);

      // Reset form state and capture initial values for change tracking
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (talentData) {
        const formValues = {
          workExperiences: talentData.workExperiences || [
            {
              jobType: "",
              companyName: "",
              startDate: null,
              endDate: null,
            },
          ],
          ...talentData,
        };
        methods.reset(formValues, { keepDirty: false, keepTouched: false });

        // Capture initial values for change tracking
        setTimeout(() => {
          const currentValues = methods.getValues();
          initialValuesRef.current = { ...currentValues };
          isFormInitializedRef.current = true;
          setIsChangeValue(false);
        }, 0);
      }

      setIsLoadingEditMode(false);
    } catch (error) {
      setIsLoadingEditMode(false);
    }
  };

  const handleCancelAttempt = () => {
    if (isChangeValue) {
      setPendingAction("cancel");
      setShowConfirmLeave(true);
    } else {
      performCancel();
    }
  };

  const handleCloseAttempt = () => {
    if (isEditMode && isChangeValue) {
      setPendingAction("close");
      setShowConfirmLeave(true);
    } else {
      onClose();
    }
  };

  const performCancel = () => {
    setIsEditMode(false);
    setIsChangeValue(false);
    isFormInitializedRef.current = false;
    initialValuesRef.current = null;
    if (talentData) {
      methods.reset({
        workExperiences: talentData.workExperiences || [
          {
            jobType: "",
            companyName: "",
            startDate: null,
            endDate: null,
          },
        ],
        ...talentData,
      });
    }
  };

  const handleConfirmLeave = () => {
    setShowConfirmLeave(false);
    if (pendingAction === "cancel") {
      performCancel();
    } else if (pendingAction === "close") {
      onClose();
    }
    setPendingAction(null);
  };

  const handleCancelLeave = () => {
    setShowConfirmLeave(false);
    setPendingAction(null);
  };

  const onSubmit = async (formData: TTalentForm, e?: React.BaseSyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Submitting before the certificate master list has loaded would drop every
    // certificate, so wait for it exactly as we already do for languageTestData.
    if (!talentId || !languageTestData || !specificSkillCertificateData?.length) return;

    try {
      const payload = convertTalentFormDataReq(formData, languageTestData, specificSkillCertificateData);
      await updateTalentApi(talentId, payload);
      toast.success(API_SUCCESS_MESSAGE);

      await refetch();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALENT.FETCH_DETAIL, talentId] });

      if (caseId) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_MATCHES, Number(caseId)] });
      }

      setIsEditMode(false);
      setIsChangeValue(false);
      isFormInitializedRef.current = false;
      initialValuesRef.current = null;
    } catch (error) {
      showError(error);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        hideBackdrop={false}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          handleCloseAttempt();
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 0,
          "& .MuiBackdrop-root": {
            padding: 0,
          },
        }}
      >
        <ModalContainer
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        >
          <Stack
            direction="row"
            bgcolor={background.disable}
            height="56px"
            minHeight="56px"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
          >
            <Typography variant="body14Light" color={"#0B0C0D"}>
              人材情報
            </Typography>
            <IconBox onClick={handleCloseAttempt} sx={{ p: 0 }}>
              <IconClose color={black[80]} fontSize={24} />
            </IconBox>
          </Stack>

          {isEditMode ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflow: "hidden",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              <Box
                data-modal-form="talent-info"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  overflow: "hidden",
                  "& form": {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                  },
                }}
              >
                <Form methods={methods} onSubmit={onSubmit} schema={talentSchema as typeof talentSchema} enableBlocker={false}>
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      width: "100%",
                    }}
                  >
                    {!talentId ? null : isLoading || !talentData || isLoadingEditMode ? (
                      <LoadingPage />
                    ) : (
                      <Grid container justifyContent="center">
                        <Grid item xs={12} sx={{ px: "32px" }}>
                          <Box mt={4}>
                            <TalentFormRecruitmentStatus personInChargeId={talentData.personInChargeId} matchOfficeId={talentData.matchOfficeId} />
                          </Box>
                          <TalentFormBasicInfo />
                          <TalentFormResidenceStatus />
                          <TalentFormLanguageAbility
                            onValidationCheck={() => true}
                            onContinueSubmit={() => {}}
                          />
                          <TalentFormEducationBackground />
                          <TalentFormWorkExperience />
                          <Box ref={certificateFilesRef}>
                            <TalentFormCertificateFiles
                              onFileChange={() => {}}
                              isEditMode={true}
                            />
                          </Box>
                          <TalentFormMemo />
                          <TalentFormInterviewEvaluation />
                          <TalentFormFamilyInfo />
                        </Grid>
                      </Grid>
                    )}
                  </Box>

                  <Box>
                    <hr color={divider.high} />
                    <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCancelAttempt();
                        }}
                        sx={{ width: "102px" }}
                      >
                        キャンセル
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        startIcon={<IconSave />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          methods.handleSubmit(onSubmit)(e);
                        }}
                        sx={{ width: "88px" }}
                      >
                        保存
                      </Button>
                    </Stack>
                  </Box>
                </Form>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1, overflowY: "auto", px: 4, pb: 4 }}>
                {!talentId ? null : isLoading || !talentData ? (
                  <LoadingPage />
                ) : (
                  <Grid container spacing={4} justifyContent="center">
                    <Grid item>
                      <Box mt={4}>
                        <TalentRecruitmentStatus defaultValues={talentData} />
                      </Box>
                      <TalentBasicInfo defaultValues={talentData} />
                      <TalentResidenceStatusInfo defaultValues={talentData} />
                      <TalentLanguageAbility defaultValues={talentData} />
                      <TalentEducationBackground defaultValues={talentData} />
                      <TalentWorkExperience defaultValues={talentData} />
                      <TalentCertificateFiles defaultValues={talentData} />
                      <TalentMemo defaultValues={talentData} />
                      <TalentInterviewEvaluation defaultValues={talentData} />
                      <TalentFamilyInfo defaultValues={talentData} />
                    </Grid>
                  </Grid>
                )}
              </Box>

              <Box>
                <hr color={divider.high} />
                <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px">
                  <Button variant="ghost" size="sm" onClick={onClose} sx={{ width: "102px" }}>
                    キャンセル
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEdit}
                    startIcon={<IconEditor />}
                    disabled={isLoadingEditMode || isLoadingLanguageTest}
                    sx={{ width: "158px" }}
                  >
                    人材情報を編集
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </ModalContainer>
      </Modal>

      <ConfirmModal isOpen={showConfirmLeave} onClose={handleCancelLeave} onOk={handleConfirmLeave} labelClose="いいえ" labelOk="はい" width={500}>
        <Stack mb={2}>
          <Typography>入力が完了していません。このまま戻ってもよろしいですか？</Typography>
        </Stack>
      </ConfirmModal>
    </>
  );
};

const ModalContainer = styled(Box)`
  width: 764px;
  height: 728px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  margin: auto;
  &:focus {
    outline: none;
  }
`;
